<template>
  <div v-if="currentCustomer">
    <b-form @submit.prevent="onSubmit" @keydown="form.errors.clear($event.target.name)">
      <template v-if="!readonly">
        <textarea
          v-model.trim="form.detail"
          name="detail"
          rows="3"
          class="job-note form-control"
          placeholder="Type a customer note here..."
        ></textarea>

        <b-card-footer style="border-top: 0">
          <div class="row">
            <div class="col">
              <div class="d-flex">
                <button
                  v-if="!editingUpload"
                  class="btn btn-primary btn-sm mr-3"
                  :disabled="form.notReady() || (!form.detail && !form.file)"
                >
                  <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
                  <font-awesome-icon v-else :icon="['fal', 'plus-circle']" />
                  Add Text
                </button>

                <file-uploader
                  v-show="form.detail.length === 0"
                  :file-url="form.file"
                  :data="{ customer_id: currentCustomer.id, action: form.action }"
                  post-action="customer-notes"
                  @uploaded="fileUploaded"
                  @editing="editingUpload = true"
                  @notEditing="editingUpload = false"
                >
                  <font-awesome-icon :icon="['fal', 'cloud-upload']" />
                  Attach File
                </file-uploader>
              </div>
            </div>
          </div>
        </b-card-footer>
      </template>
      <template v-else>
        <header class="job-header row">
          <div class="main-heading">
            <b>Technician #{{ currentCustomer.name }}</b>
          </div>
        </header>
      </template>

      <div class="row no-print">
        <div class="col-8">
          <div class="mt-3">
            <b-button-group>
              <b-button :variant="type === 'all' ? 'success' : ''" @click.prevent="type = 'all'"
                >All</b-button
              >
              <b-button :variant="type === 'notes' ? 'warning' : ''" @click.prevent="type = 'notes'"
                >Notes</b-button
              >
              <b-button :variant="type === 'log' ? 'info' : ''" @click.prevent="type = 'log'"
                >Log</b-button
              >
            </b-button-group>
          </div>
        </div>
        <div v-if="false" class="col-4 text-right pt-3">
          <button v-if="readonly" class="btn btn-primary no-print" @click.prevent="print">
            <font-awesome-icon :icon="['fal', 'file-pdf']" />
            Print
          </button>

          <b-button v-else variant="primary" @click.prevent="exportLog"> Export Log </b-button>
        </div>
      </div>

      <ul class="list-group overflow-y-auto mt-3" :style="readonly ? '' : 'max-height: 552px'">
        <!-- eslint-disable -->
        <li class="list-group-item activity" :key="activity.id + activity.action" v-for="activity in activities"
            v-if="type === 'all' || (type === 'notes') && activity.type === 'note' || (type === 'log') && activity.type === 'log'"
        >
          <div class="row no-gutters">
            <div class="col-auto">
              <div class="highlight text-center">
                <font-awesome-icon :icon="getOperationIcon(activity)" :class="getOperationClass(activity)"/>
              </div>
            </div>
            <div class="col">
              <template v-if="activity.type === 'log'">
                {{ activity.action }}:

                <generic-object v-for="(content, key) in activity.detail.diff"
                                :key="activity.id + key" :copy-to-clipboard="true"
                                :tooltip="changeTooltip(content, key, activity.detail.before, activity.action)">
                  {{ key.replace(/_/g, ' ') | capitalize }}
                </generic-object>
              </template>
              <template v-else>
                <div class="whitespace-preline">{{ activity.detail }}</div>
              </template>

              <div v-if="$lodash.has(activity, 'media[0].url')" class="w-100">
                <div v-if="
                    activity.media[0].mime_type === 'image/jpeg'
                    ||
                    activity.media[0].mime_type === 'image/jpg'
                    ||
                    activity.media[0].mime_type === 'image/gif'
                    ||
                    activity.media[0].mime_type === 'image/png'
                  ">
                  <img width="100" v-img="{
                      src: activity.media[0].url,
                      sourceButton: true,
                      group: 'activities'
                    }"
                    :src="activity.media[0].url" alt="">
                </div>
                <div v-else-if="activity.media[0].mime_type === 'application/pdf'">
                  View attached
                  <pdf-object :href="activity.media[0].url"/>
                </div>
                <div v-else>
                  View attached <a :href="activity.media[0].url" target="_blank">file</a>
                </div>
              </div>

              <div class="text-muted">
                {{ formatLocalDate(activity.created_at) }}
                by
                {{ activity.updated_by ? activity.updated_by.name : 'Anonymous' }}
              </div>
            </div>
          </div>
        </li>
        <li class="list-group-item activity text-center"
            v-if="type === 'all' && activities.length === 0"
        >
          No activities yet
        </li>
      </ul>

      <div class="mt-3">
        <button class="btn btn-primary" @click.prevent="loadMore"
                :disabled="!lastPage || this.page >= lastPage || form.busy || loadingMore"
                v-if="!readonly"
        >
          <font-awesome-icon spin :icon="['fal', 'spinner']" v-if="form.busy || loadingMore"/>
          <font-awesome-icon :icon="['fal', 'caret-down']" v-else/>
          Load more
        </button>
      </div>
    </b-form>
  </div>
</template>

<script type="text/babel">
import format from "../../../mixins/format";
import FileUploader from "../../../components/common/FileUploader.vue";

const initialState = {
  id: "",
  customer_id: "",
  action: "",
  detail: "",
  file: "",
};

export default {
  components: {
    FileUploader,
  },
  mixins: [format],

  props: {
    currentCustomer: {
      required: true,
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      initialised: false,
      type: this.readonly ? "log" : "notes",
      activities: [],

      perPage: 10,
      page: 0,
      lastPage: null,
      loadingMore: false,

      editingUpload: false,

      form: new window.AppForm(initialState),
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    init() {
      this.form.reset();
      this.form.customer_id = this.currentCustomer.id;

      this.activities = [];
      this.page = 0;
      this.lastPage = null;
      this.loadingMore = false;

      return this.loadMore();
    },

    /* eslint-disable no-param-reassign */
    loadMore() {
      if (this.page < this.lastPage || this.lastPage === null) {
        const page = this.page + 1;
        this.loadingMore = true;

        this.form.startProcessing();

        const limit = this.readonly ? 99999999 : this.perPage;

        // noinspection JSUnresolvedFunction
        this.$http
          .get(`/customers/${this.currentCustomer.id}/activities?page=${page}&limit=${limit}`)
          .then((response) => {
            // Unpack the JSON in detail property
            if (response.body.data && response.body.data.length > 0) {
              this.page = response.body.current_page;
              this.lastPage = response.body.last_page;

              try {
                response.body.data.forEach((activity, index) => {
                  if (activity.action && activity.detail) {
                    response.body.data[index].detail = JSON.parse(activity.detail);
                    response.body.data[index].type = "log";
                  } else {
                    response.body.data[index].type = "note";
                  }
                });
              } catch (error) {
                window.toastr.error("There seems to be some problem with activity data.", "Oops");
              }
            }

            this.activities = this.activities.concat(response.body.data);

            this.page = response.body.current_page;
            this.lastPage = response.body.last_page;

            this.initialised = true;
            this.loadingMore = false;
            this.form.finishProcessing();
          })
          .catch(() => {
            this.initialised = true;
            this.loadingMore = false;
            this.form.finishProcessing();
          });
      }
    },

    onSubmit() {
      if (!this.form.detail) {
        return;
      }

      this.form.post("/customer-notes").then(() => {
        this.$nextTick(() => this.init());

        window.toastr.success("Note added!", "Success");
      });
    },

    getOperationClass(activity) {
      switch (activity.action) {
        case "Customer profile created":
        case "Customer address created":
        case "Customer contact created":
          return "successful";

        case "Customer address updated":
        case "Customer contact updated":
        case "Customer profile updated":
          return "update";

        default:
          return "";
      }
    },

    getOperationIcon(activity) {
      switch (activity.action) {
        case "Customer profile created":
        case "Customer profile updated":
          return ["fal", "user-edit"];

        case "Customer address created":
        case "Customer address updated":
          return ["fal", "map-marked-alt"];

        case "Customer contact created":
        case "Customer contact updated":
          return ["fal", "male"];

        case null:
          if (activity.media.length > 0) {
            if (
              activity.media[0].mime_type === "image/jpeg" ||
              activity.media[0].mime_type === "image/jpg" ||
              activity.media[0].mime_type === "image/gif" ||
              activity.media[0].mime_type === "image/png"
            ) {
              if (this.$lodash.get(activity.media[0], "file_name") === "signature.jpg") {
                return ["fal", "signature"];
              }

              return ["fal", "image"];
            }

            return ["fal", "paperclip"];
          }
          return ["fal", "user-edit"];

        default:
          return "";
      }
    },

    fileUploaded() {
      window.toastr.success("Attachment added!", "Success");

      this.init();
    },

    isArray(obj) {
      return !!obj && obj.constructor === Array;
    },

    changeTooltip(content, key, before, action) {
      let changeFrom;
      let changeTo;

      if (this.isArray(content)) {
        changeFrom = this.$lodash.get(before, key, []).join(", ");
        changeTo = content.join(", ");
      } else {
        changeFrom = this.$lodash.get(before, key, "");
        changeTo = content;
      }

      if (action === "Customer profile created") {
        return String(changeTo);
      }

      return `Changed from: '${changeFrom}' to: '${changeTo}'`;
    },

    print() {
      window.print();
    },

    exportLog() {
      window.open(`/customers/${this.currentCustomer.id}/logs`);
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.activity {
  padding: 10px 8px 6px 0;

  font-size: 13px;
  line-height: 24px;
}

.highlight {
  width: 50px;

  i {
    font-size: 25px;
    color: #919191;
  }

  .successful {
    color: #79cc9b;
  }

  .update {
    color: #f7c16b;
  }

  .destroy {
    color: #d9534f;
  }
}

.badge {
  margin-right: 4px;
}
</style>
